import { flow, makeObservable, observable } from 'mobx';
import { flowLoading, toGenerator } from '../../utils';
import WalletConnectClient, { EVM_NS } from './Client';
import { WalletConnectChainID, WALLETCONNET_PROJECT_ID } from './constants';
import { ApiGetWalletsResponse, WcWallet } from './types';
import { w3fetchBlob, w3mget } from './utils';

const sdkProperties = {
	projectId: WALLETCONNET_PROJECT_ID,
	st: 'appkit',
	sv: 'react-ethers,solana,bitcoin-1.8.1',
};

function walletFilter(w: WcWallet) {
	return w.supports_wc;
}

function intersection<T>(a: T[], b: T[]): T[] {
	const setB = new Set(b);
	return a.filter((x) => setB.has(x));
}

export default class WCWalletList {
	private readonly root: WalletConnectClient;
	page = 1;
	loaded: number | null = null;
	entries: WcWallet[] = [];
	images: Record<string, { blob: Blob; url: string } | { loading: Promise<Blob> }> = {};
	constructor(root: WalletConnectClient) {
		this.root = root;
		makeObservable(this, { entries: observable.shallow, images: observable });
	}

	get chains() {
		return [...this.solanaChains, ...this.evmChains];
	}

	get solanaChains() {
		return [WalletConnectChainID.Mainnet];
	}

	get evmChains() {
		return this.root.evmChains.map((c) => `${EVM_NS}:${c.chainId}`);
	}

	fetchImage = flow(function* (this: WCWalletList, entry: WcWallet) {
		const imageId = entry.image_id;
		const id = entry.id;
		if (!imageId) return;
		let current = this.images[id];
		if (current) {
			if ('url' in current) {
				return;
			}
			yield current.loading;
			return;
		}

		const promise = w3fetchBlob({ path: `getWalletImage/${imageId}`, params: { ...sdkProperties } });
		current = this.images[id] = { loading: promise };
		const blob = yield current.loading;
		const url = URL.createObjectURL(blob);
		this.images[id] = { blob, url };
	});

	filter(type: 'solana' | 'evm') {
		const chains = type === 'solana' ? this.solanaChains : this.evmChains;
		return this.entries
			.filter((entry) => entry.mobile_link)
			.filter((entry) => intersection(entry.chains ?? [], chains).length > 0);
	}

	fetch = flowLoading(function* (this: WCWalletList, params: object) {
		const result: ApiGetWalletsResponse = yield w3mget<ApiGetWalletsResponse>({
			path: '/getWallets',
			params: {
				...sdkProperties,
				...params,
				chains: this.chains.join(','),
				page: String(this.page),
				entries: '40',
			},
		});
		return result;
	});

	loadAll = flowLoading(function* (this: WCWalletList) {
		if (this.loaded) {
			return;
		}
		while (true) {
			const result = yield* toGenerator(this.fetch({}));
			this.entries = this.entries.concat(result.data.filter(walletFilter));
			if (result.nextPage) {
				this.page = result.nextPage;
			} else {
				break;
			}
			if (this.page > 10) {
				break;
			}
		}
		this.loaded = Date.now();
	});

	cleanup() {}
}
